import axios from "axios";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Clock,
  IndianRupee,
  Loader2,
  MapPin,
  Package,
  Phone,
  Scissors,
  Tag,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RequestProductsModal from "../../../components/StaffSideComponents/myJobs/RequestProductsModal";
import VerifyOTPModal from "../../../components/StaffSideComponents/myJobs/VerifyOTPModal";
import { formatDate } from "../../../utils/helpers";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";
import {
  getConfirmationConfig,
  getNextStatus,
  statusConfig,
} from "./StaffMyJobs";
import { useUpiPaymentQR } from "../../../hooks/useUpiPaymentQR";
import {
  ADMIN_NAV_HEIGHT,
  STAFF_ACTION_BAR_HEIGHT,
} from "../../../utils/constants";
import PaymentStatusBadge from "../../../components/common/PaymentStatusBadge";
import useIsDesktop from "../../../hooks/useIsDesktop";
import uploadFileToCloudinary from "./../../../utils/uploadFileToCloudinary";
import AddServiceToBookingModal from "./AddServiceToBookingModal";

const StaffJobDetails = () => {
  const navigate = useNavigate();
  const params = useParams();

  const isPackage = params.type === "package";
  const bookingId = params.bookingId;

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [staffId, setStaffId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const { qrImage, loading: qrLoading, initAmountOnlyQR } = useUpiPaymentQR();
  const isDesktop = useIsDesktop();
  const [uploadingProof, setUploadingProof] = useState(false);
  const fileInputRef = useRef(null);
  const [showProofPreview, setShowProofPreview] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Discount — only for service bookings
  const [discountInput, setDiscountInput] = useState("");
  const [savingDiscount, setSavingDiscount] = useState(false);

  useEffect(() => {
    if (bookingId) fetchBookingDetails();
  }, [bookingId]);


  useEffect(() => {
    if (booking) {
      setDiscountInput(String(booking.finalPayableAmount ?? booking.totalAmount));
    }
  }, [booking]);

  // ── Endpoint helpers ──────────────────────────────────────────────────────
  const fetchEndpoint = isPackage
    ? `${process.env.REACT_APP_API_URL}/package-bookings/${bookingId}`
    : `${process.env.REACT_APP_API_URL}/service-bookings/get-by-id/${bookingId}`;

  const statusUpdateUrl = isPackage
    ? `${process.env.REACT_APP_API_URL}/package-bookings/update-status`
    : `${process.env.REACT_APP_API_URL}/staff-assignment/update-status`;

  const paymentUpdateUrl = (id) =>
    isPackage
      ? `${process.env.REACT_APP_API_URL}/package-bookings/update-payment-status/${id}`
      : `${process.env.REACT_APP_API_URL}/service-bookings/update-payment-status/${id}`;

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchBookingDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(fetchEndpoint);
      const data = response.data.data;
      setBooking(data);
      if (data?.staffId) setStaffId(data.staffId._id || data.staffId);
    } catch (error) {
      console.error("Failed to fetch booking:", error);
      showError("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };


  // ── Payment handlers ──────────────────────────────────────────────────────
  const handlePayViaUpi = async () => {
    const amountToCollect =
      booking?.payment?.status === "partially_paid"
        ? booking?.payment?.remainingAmount
        : booking?.finalPayableAmount ?? booking?.totalAmount;

    await initAmountOnlyQR({ amount: amountToCollect });
    setShowUpiModal(true);
  };

  const handleCollectCash = async () => {
    const confirmed = await confirmAction({
      title: "Confirm Cash Collection",
      text: "Have you collected the full amount in cash?",
      confirmText: "Yes, Collected",
      cancelText: "Cancel",
      icon: "question",
    });
    if (!confirmed) return;

    try {
      await axios.patch(paymentUpdateUrl(booking._id), {
        status: "paid",
        settledVia: "CASH",
      });
      await fetchBookingDetails();
    } catch (error) {
      showError("Failed to update payment");
    }
  };

  // ── Status action ─────────────────────────────────────────────────────────
  const handleStatusAction = async () => {
    if (!booking || updating) return;

    const nextStatus = getNextStatus(booking.status);
    if (!nextStatus) return;

    if (
      (booking.status === "confirmed" || booking.status === "rescheduled") &&
      nextStatus === "ongoing"
    ) {
      setShowOTPModal(true);
      return;
    }

    const confirmation = getConfirmationConfig(booking.status, nextStatus);
    if (confirmation) {
      const confirmed = await confirmAction(confirmation);
      if (!confirmed) return;
    }

    setUpdating(true);
    try {
      await axios.put(statusUpdateUrl, {
        bookingId: booking._id,
        status: nextStatus,
      });
      setBooking((prev) => ({ ...prev, status: nextStatus }));
      showSuccess("Status updated successfully");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const continueStartWork = async () => {
    setUpdating(true);
    try {
      await axios.put(statusUpdateUrl, {
        bookingId: booking._id,
        status: "ongoing",
      });
      setBooking((prev) => ({ ...prev, status: "ongoing" }));
      showSuccess("Work started successfully");
    } catch (error) {
      showError("Failed to start work");
    } finally {
      setUpdating(false);
      setShowOTPModal(false);
    }
  };

  // ── Upload payment proof (service + package bookings) ────────────────────
  const proofUploadUrl = (id) =>
    isPackage
      ? `${process.env.REACT_APP_API_URL}/package-bookings/upload-payment-proof/${id}`
      : `${process.env.REACT_APP_API_URL}/service-bookings/upload-payment-proof/${id}`;

  const handleUploadPaymentProof = async (file) => {
    if (!file || uploadingProof) return;
    try {
      setUploadingProof(true);
      const { uploadPromise } = uploadFileToCloudinary(file);
      const result = await uploadPromise;
      if (!result?.url) { showError("Upload failed"); return; }

      await axios.patch(proofUploadUrl(booking._id), { proofUrl: result.url, staffId });

      setBooking((prev) => ({
        ...prev,
        payment: {
          ...prev.payment,
          proof: { url: result.url, uploadedAt: new Date(), uploadedBy: staffId },
        },
      }));
      showSuccess("Payment proof uploaded successfully");
    } catch (err) {
      showError(err.response?.data?.message || "Failed to upload payment proof");
    } finally {
      setUploadingProof(false);
    }
  };

  // ── Discount — works for both service and package bookings ──────────────────
  const totalAmount = booking?.totalAmount ?? 0;
  const couponCode = booking?.coupon?.code || null;
  const couponDiscount = booking?.coupon?.discountApplied || 0;
  const parsedInput = discountInput !== "" ? Number(discountInput) : null;
  const liveFinalPayable =
    parsedInput !== null && parsedInput <= totalAmount
      ? parsedInput
      : booking?.finalPayableAmount ?? totalAmount;
  const liveDiscount =
    liveFinalPayable < totalAmount ? totalAmount - liveFinalPayable : 0;
  const liveDiscountPct =
    totalAmount > 0 && liveDiscount > 0
      ? ((liveDiscount / totalAmount) * 100).toFixed(1)
      : "0";

  const handleSaveDiscount = async () => {
    const parsedFinalInput = Number(discountInput);
    if (parsedFinalInput > totalAmount) {
      return showError("Final amount cannot exceed total");
    }
    const calculatedDiscount =
      parsedFinalInput < totalAmount ? totalAmount - parsedFinalInput : 0;

    setSavingDiscount(true);
    try {
      if (isPackage) {
        // Package: dedicated discount endpoint — no services array needed
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/package-bookings/apply-discount/${booking._id}`,
          { manualDiscount: calculatedDiscount },
        );
      } else {
        // Service: update-services endpoint (also recalculates total)
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/service-bookings/update-services/${booking._id}`,
          {
            services: booking.services.map((s) => ({
              serviceId: s.serviceId._id || s.serviceId,
              brand: s.brand,
              qty: s.qty,
            })),
            manualDiscount: calculatedDiscount,
          },
        );
      }
      await fetchBookingDetails();
      showSuccess("Discount applied successfully");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to apply discount");
    } finally {
      setSavingDiscount(false);
    }
  };

  // ── Derived flags ─────────────────────────────────────────────────────────
  const scheduleDate = new Date(
    booking?.rescheduledDate || booking?.slotAvailabilityId?.date,
  );
  const isFutureSchedule = scheduleDate > new Date();

  // const canUploadProof = isPaymentDone && !hasProof;
  const canUploadProof =
    booking?.status === "completed" &&
    booking?.payment?.paidAmount > 0 &&
    !booking?.payment?.proof?.url;

  const paymentProofUrl = booking?.payment?.proof?.url;
  const paymentProofUploadedAt = booking?.payment?.proof?.uploadedAt;
  const isPaymentCompleted = booking?.payment?.status === "paid";

  // service-only
  const canAddServices =
    !isPackage &&
    ["confirmed", "rescheduled", "ongoing"].includes(booking?.status) &&
    !isPaymentCompleted &&
    booking?.status !== "cancelled";

  // Discount available for both service and package bookings at completion
  const canApplyDiscount =
    booking?.status === "completed" &&
    booking?.payment?.method === "PAY_AFTER_SERVICE" &&
    !isPaymentCompleted;

  // Unified final payable (used for package display and payment amounts)
  const pkgFinalPayable = booking?.finalPayableAmount || booking?.totalAmount || 0;

  return (
    <div
      style={{ paddingBottom: `${STAFF_ACTION_BAR_HEIGHT}px` }}
      className="min-h-screen relative bg-gray-50 pb-20"
    >
      {/* Bottom Action Bar */}
      {booking && booking.status !== "cancelled" && (
        <div
          style={!isDesktop ? { bottom: `${ADMIN_NAV_HEIGHT}px` } : { bottom: 0 }}
          className="fixed left-0 lg:left-52 right-0 z-20 bg-white border-t border-gray-200"
        >
          <div className="w-full max-w-3xl mx-auto p-3">
            {booking.status === "completed" &&
              booking.payment?.method === "PAY_AFTER_SERVICE" &&
              booking.payment?.status !== "paid" ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleCollectCash}
                  className="h-12 rounded-xl bg-green-600 text-white font-semibold text-sm active:scale-[0.98]"
                >
                  Collect Cash
                </button>
                <button
                  onClick={handlePayViaUpi}
                  className="h-12 rounded-xl border border-[#F7BFAD] bg-[#F7BFAD]/40 text-[#f0653a] font-semibold text-sm active:scale-[0.98]"
                >
                  Pay via UPI
                </button>
              </div>
            ) : (
              booking.status !== "completed" && (
                <button
                  disabled={updating}
                  onClick={handleStatusAction}
                  className={`w-full h-12 rounded-xl text-white font-semibold text-sm active:scale-[0.98] ${statusConfig[booking.status]?.btnColor}`}
                >
                  {updating ? "Updating…" : statusConfig[booking.status]?.action}
                </button>
              )
            )}
            {canUploadProof && (
              <>
                <div className="bg-white shadow-sm border border-gray-200 p-4 mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={16} className="text-orange-500" />
                    <h3 className="text-sm font-bold text-gray-800">Upload Payment Proof</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Please upload a photo of the cash receipt or UPI confirmation.
                  </p>
                  <button
                    disabled={uploadingProof}
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-11 rounded-xl bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98]"
                  >
                    {uploadingProof ? (
                      <><Loader2 className="animate-spin" size={16} />Uploading…</>
                    ) : (
                      <><Package size={16} />Upload Payment Proof</>
                    )}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => handleUploadPaymentProof(e.target.files[0])}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-primaryColor/10 flex justify-between px-4 py-3 backdrop-blur border-b border-primaryColor/10">
        <div className="flex items-center">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg transition">
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <div className="leading-tight">
            <h1 className="text-base md:text-lg font-semibold text-primaryTextColor flex items-center gap-2">
              {isPackage && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-semibold">
                  <Package size={10} /> Package
                </span>
              )}
              Booking Details
            </h1>
            <p className="text-[0.6em] sm:text-xs text-secondaryTextColor">
              ID: {bookingId?.slice(0, 8)?.toUpperCase()}
            </p>
          </div>
        </div>
        <div className="flex items-center">
          {booking && (
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${statusConfig[booking.status]?.color}`}
            >
              {statusConfig[booking.status]?.label}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-gray-400" size={40} />
        </div>
      ) : (
        <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-3">

          {/* Customer Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <User size={16} /> Customer Information
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name</span>
                <span className="font-medium">{booking?.userId?.name || "N/A"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone</span>
                <a
                  href={`tel:${booking?.userId?.phoneNumber}`}
                  className="font-medium text-blue-600 flex items-center gap-1"
                >
                  <Phone size={14} />
                  {booking?.userId?.phoneNumber || "N/A"}
                </a>
              </div>
            </div>
          </div>

          {/* ── PACKAGE: show package details ── */}
          {isPackage && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Package size={16} />
                Package
              </h3>
              <div className="mb-3">
                <p className="font-semibold text-gray-900">
                  {booking?.packageSnapshot?.name || booking?.packageId?.name || "Package"}
                </p>
                {(booking?.packageSnapshot?.category || booking?.packageId?.category) && (
                  <p className="text-xs text-gray-400 uppercase tracking-wide mt-0.5">
                    {booking?.packageSnapshot?.category || booking?.packageId?.category}
                  </p>
                )}
              </div>
              {/* Included services — string list from packageSnapshot */}
              {(() => {
                const services =
                  booking?.packageSnapshot?.services ||
                  booking?.packageId?.services ||
                  [];
                return services.length > 0 ? (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Included Services ({services.length})
                    </p>
                    <div className="flex flex-col gap-2">
                      {services.map((svc, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 border border-gray-200 rounded-lg p-2.5 bg-gray-50"
                        >
                          <div className="w-6 h-6 rounded-full bg-accentTextColor/10 flex items-center justify-center flex-shrink-0">
                            <Scissors size={12} className="text-accentTextColor" />
                          </div>
                          <span className="text-sm font-medium text-gray-800">{svc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* ── SERVICE: show service details (original, unchanged) ── */}
          {!isPackage && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Scissors size={16} />
                Services ({booking?.services?.length || 0})
              </h3>
              <div className="space-y-3">
                {booking?.services?.map((service, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">
                          {service?.serviceId?.name || "Service"}
                        </p>
                        <p className="text-xs text-gray-500">
                          ₹{service?.finalPrice?.toFixed(2)} × {service.qty}
                        </p>
                        {service.brand && (
                          <p className="text-xs text-gray-500">Brand: {service?.brand}</p>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-gray-800">
                        ₹{((service?.finalPrice ?? 0) * (service?.qty ?? 1)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {service?.serviceId?.duration || "N/A"}
                      </span>
                      <span>•</span>
                      <span>{service?.serviceId?.categoryId?.categoryName || "N/A"}</span>
                    </div>
                  </div>
                ))}
              </div>
              {canAddServices && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="w-full h-11 border border-primaryBtnColor text-primaryBtnColor rounded-xl font-semibold my-3"
                >
                  + Add More Services
                </button>
              )}
            </div>
          )}

          {/* Payment & Amount */}
          <div className="grid grid-cols-1 gap-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-4">
              {booking?.payment?.status !== "paid" && (
                <span className="text-[0.7em] text-red-500 bg-red-50 border border-red-100 px-2 py-1 rounded-lg">
                  Pending Collection
                </span>
              )}

              <div className="border-t border-dashed border-gray-200" />

              {/* Discount section — SERVICE ONLY */}
              {canApplyDiscount && (
                <>
                  <div>
                    {booking?.payment?.status === "partially_paid" && (
                      <div className="mb-2 text-xs text-blue-600 bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg">
                        Advance Paid: ₹{booking.payment.paidAmount} — Remaining will auto-adjust after discount.
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mb-2">
                      <Tag size={14} className="text-gray-500" />
                      <p className="text-sm font-semibold text-gray-700">Apply Discount</p>
                    </div>
                    <label className="text-xs text-gray-400 block mb-1.5">
                      Enter final payable amount (discount auto-calculated)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={discountInput}
                        onChange={(e) => setDiscountInput(e.target.value)}
                        placeholder={`₹${totalAmount.toFixed(2)}`}
                        min={0}
                        max={totalAmount}
                        className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primaryBtnColor/30 focus:border-primaryBtnColor"
                      />
                      <button
                        onClick={handleSaveDiscount}
                        disabled={savingDiscount}
                        className="h-10 px-4 rounded-xl bg-primaryBtnColor text-white text-sm font-semibold flex items-center gap-1.5 active:scale-[0.98] disabled:opacity-60"
                      >
                        {savingDiscount ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        {savingDiscount ? "Saving…" : "Apply"}
                      </button>
                    </div>
                    {liveDiscount > 0 && (
                      <div className="mt-3 bg-green-50 border border-green-100 rounded-xl px-3 py-2 flex items-center justify-between">
                        <span className="text-sm text-green-700 font-medium">
                          Discount: ₹{liveDiscount.toFixed(2)}
                        </span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                          {liveDiscountPct}% OFF
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-dashed border-gray-200" />
                </>
              )}

              <div className="border-t border-dashed border-gray-200" />

              {/* Final payable */}
              <div className="flex items-center justify-between">
                <div className="space-y-3 w-full">

                  {/* ── AMOUNT BREAKDOWN (SERVER DRIVEN) ── */}
                  <div className="space-y-2 text-sm">

                    {/* SERVICE TOTAL */}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Service Total</span>
                      <span className="font-medium">
                        ₹{booking?.totalAmount?.toFixed(2)}
                      </span>
                    </div>

                    {/* COUPON */}
                    {booking?.coupon?.discountApplied > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>
                          Coupon {booking?.coupon?.code && `(${booking.coupon.code})`}
                        </span>
                        <span>
                          −₹{booking.coupon.discountApplied.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* MANUAL DISCOUNT */}
                    {booking?.manualDiscount > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Manual Discount</span>
                        <span>
                          −₹{booking.manualDiscount.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* FINAL */}
                    <div className="flex justify-between border-t pt-3 mt-2">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">
                        Final Payable
                      </span>
                      <span className="text-lg font-bold text-green-700">
                        ₹{booking?.finalPayableAmount?.toFixed(2)}
                      </span>
                    </div>

                  </div>

                  {/* ── PAYMENT STATE (SERVER DRIVEN) ── */}
                  {booking?.payment?.status === "paid" ? (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-green-600">
                        Amount Collected
                      </span>
                      <span className="text-2xl font-bold text-green-600">
                        ₹{booking?.payment?.paidAmount?.toFixed(2)}
                      </span>
                    </div>
                  ) : booking?.payment?.status === "partially_paid" ? (
                    <>
                      <div className="flex justify-between text-sm text-green-700">
                        <span>Already Paid</span>
                        <span className="font-semibold">
                          ₹{booking?.payment?.paidAmount?.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-red-600">
                          Amount To Collect
                        </span>
                        <span className="text-2xl font-bold text-red-600">
                          ₹{booking?.payment?.remainingAmount?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-primaryBtnColor">
                        Amount To Collect
                      </span>
                      <span className="text-2xl font-bold text-primaryBtnColor">
                        ₹{booking?.finalPayableAmount?.toFixed(2)}
                      </span>
                    </div>
                  )}

                </div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3">
              <p className="text-[0.7em] text-gray-500 mb-2">Payment Status</p>
              <div className="flex items-center my-auto justify-center">
                <PaymentStatusBadge
                  status={booking?.payment?.status}
                  settledVia={booking?.payment?.settledVia}
                />
              </div>
              {paymentProofUrl && (
                <div className="bg-white mt-3 rounded-2xl shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Package size={16} className="text-green-600" />
                    <h3 className="text-sm font-bold text-gray-800">Payment Proof</h3>
                  </div>
                  <div
                    onClick={() => setShowProofPreview(true)}
                    className="relative w-full h-40 rounded-xl overflow-hidden border cursor-pointer group"
                  >
                    <img src={paymentProofUrl} alt="Payment Proof" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">Tap to view</span>
                    </div>
                  </div>
                  {paymentProofUploadedAt && (
                    <p className="text-[11px] text-gray-400 mt-2">
                      Uploaded at: {new Date(paymentProofUploadedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                <Clock size={16} className="text-blue-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-800">Schedule</h3>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Service Date</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(booking?.rescheduledDate || booking?.slotAvailabilityId?.date)}
                </p>
              </div>
              <div className="h-8 w-px bg-gray-200" />
              <div className="text-right">
                <p className="text-xs text-gray-500">Time Slot</p>
                <p className="text-sm font-medium text-gray-700">
                  {booking?.slotSnapshot?.startTime} – {booking?.slotSnapshot?.endTime}
                </p>
              </div>
            </div>
            {isFutureSchedule && (
              <div className="mt-3 text-[0.7em] text-gray-400">Please arrive a few minutes early</div>
            )}
          </div>

          {/* Location */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                <MapPin size={16} className="text-green-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-800">Service Location</h3>
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {booking?.location?.address?.locality}, {booking?.location?.address?.city}
            </p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              {booking?.location?.address?.formatted || "N/A"}
            </p>
            {booking?.location?.coordinates && (
              <a
                href={`https://www.google.com/maps?q=${booking.location.coordinates[1]},${booking.location.coordinates[0]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center w-full h-11 rounded-xl border border-green-200 bg-green-50 text-green-700 font-semibold text-sm active:scale-[0.98]"
              >
                Navigate on Google Maps
              </a>
            )}
          </div>
        </div>
      )}

      {/* RequestProductsModal — service only */}
      {!isPackage && (
        <RequestProductsModal
          isOpen={showRequestModal}
          onClose={() => setShowRequestModal(false)}
          bookingId={bookingId}
          staffId={staffId}
          onSuccess={() => setShowRequestModal(false)}
        />
      )}

      {/* UPI QR Modal */}
      {showUpiModal && (
        <UpiQrModal
          qrLoading={qrLoading}
          qrImage={qrImage}
          booking={booking}
          isPackage={isPackage}
          setBooking={setBooking}
          setShowUpiModal={setShowUpiModal}
          paymentUpdateUrl={paymentUpdateUrl}
        />
      )}

      {/* OTP Modal */}
      <VerifyOTPModal
        isOpen={showOTPModal}
        bookingId={bookingId}
        onClose={() => setShowOTPModal(false)}
        onVerified={continueStartWork}
      />

      {/* AddServiceToBookingModal — service only */}
      {!isPackage && (
        <AddServiceToBookingModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          booking={booking}
          refreshBooking={fetchBookingDetails}
        />
      )}

      {showProofPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowProofPreview(false)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowProofPreview(false)}
              className="absolute -top-10 right-0 text-white flex items-center gap-1 text-sm"
            >
              ✕ Close
            </button>
            <img
              src={paymentProofUrl}
              alt="Payment Proof Full View"
              className="w-full max-h-[80vh] object-contain rounded-xl bg-black"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ── UPI QR Modal ──────────────────────────────────────────────────────────────
const UpiQrModal = ({
  qrLoading,
  qrImage,
  booking,
  isPackage,
  setBooking,
  setShowUpiModal,
  paymentUpdateUrl,
}) => {
  const handleUpiPaymentSuccess = async () => {
    const confirmed = await confirmAction({
      title: "Confirm UPI Payment",
      text: "Has the customer completed the payment successfully?",
      confirmText: "Yes, Payment Done",
      cancelText: "Not Yet",
      icon: "question",
    });
    if (!confirmed) return;

    try {
      await axios.patch(paymentUpdateUrl(booking._id), {
        status: "paid",
        settledVia: "UPI_QR",
      });
      setBooking((prev) => ({
        ...prev,
        payment: { ...prev.payment, status: "paid", settledVia: "UPI_QR" },
      }));
    } catch (error) {
      console.error(error?.response?.data?.message || "Failed to Update Status");
    } finally {
      setShowUpiModal(false);
    }
  };

  const amountToCollect =
    booking?.payment?.status === "partially_paid"
      ? booking?.payment?.remainingAmount
      : booking?.finalPayableAmount ?? booking?.totalAmount;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-4 text-center border-b border-gray-100">
          <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-[#F7BFAD] flex items-center justify-center">
            <IndianRupee size={22} className="text-[#f0653a]" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Collect Payment via UPI</h3>
          <p className="mt-1 text-xs text-gray-500">
            Ask the customer to scan and complete the payment
          </p>
        </div>

        <div className="px-6 py-5 text-center">
          <div className="mb-4">
            <p className="text-xs text-gray-500">Amount to Pay</p>
            <p className="text-2xl font-bold text-[#f0653a]">₹{amountToCollect}</p>
          </div>
          {qrLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin text-[#f0653a]" size={28} />
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="rounded-2xl border border-[#F7BFAD] p-3 bg-[#F7BFAD]/30">
                <img src={qrImage} alt="UPI QR" className="w-48 h-48 object-contain" />
              </div>
              <p className="mt-3 text-[0.7em] text-gray-500">
                Supports all UPI apps (GPay, PhonePe, Paytm)
              </p>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 space-y-2">
          <button
            onClick={handleUpiPaymentSuccess}
            className="w-full h-11 rounded-xl bg-[#f0653a] text-white text-sm font-semibold shadow-sm active:scale-[0.98]"
          >
            Mark Payment as Completed
          </button>
          <button
            onClick={() => setShowUpiModal(false)}
            className="w-full h-10 rounded-xl text-xs text-gray-500 hover:bg-gray-50"
          >
            Cancel & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffJobDetails;


